/**
 * Admin feedback queue API layer (STORY-010-005).
 *
 * Types mirror `apps/api/garden/features/admin_feedback/schemas.py`. All
 * mutations are POSTs (apiClient adds the Idempotency-Key).
 */
import { apiClient } from "@/lib/api-client";

// ─────────────────────────── Types ─────────────────────────────────────────

export interface FeedbackListItem {
  id: string;
  status: string;
  message: string;
  rating: number | null;
  spam_score: number;
  has_contact_email: boolean;
  created_at: string;
}

export interface FeedbackListResponse {
  items: FeedbackListItem[];
  next_cursor: string | null;
}

export interface FeedbackDetail {
  id: string;
  user_id: string | null;
  status: string;
  message: string;
  rating: number | null;
  contact_email: string | null;
  spam_score: number;
  spam_reasons: string[];
  admin_reply_text: string | null;
  admin_reply_sent_at: string | null;
  replied_by_admin_id: string | null;
  closed_at: string | null;
  closed_by_admin_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface FeedbackReplyRequest {
  subject: string;
  body_text: string;
  body_html?: string | null;
}

// ─────────────────────────── Query keys ────────────────────────────────────

export const feedbackQueryKeys = {
  list: (filters: Record<string, unknown>) =>
    ["admin", "feedback", "list", filters] as const,
  detail: (id: string) => ["admin", "feedback", "detail", id] as const,
} as const;

// ─────────────────────────── Fetchers ──────────────────────────────────────

export interface ListFeedbackParams {
  status?: string;
  include_spam?: boolean;
  cursor?: string | null;
  limit?: number;
}

export function listFeedback(
  params: ListFeedbackParams,
): Promise<FeedbackListResponse> {
  const query = new URLSearchParams();
  if (params.status) query.set("status", params.status);
  if (params.include_spam) query.set("include_spam", "true");
  if (params.cursor) query.set("cursor", params.cursor);
  if (params.limit) query.set("limit", String(params.limit));
  const qs = query.toString();
  return apiClient.get<FeedbackListResponse>(`/admin/feedback${qs ? `?${qs}` : ""}`);
}

export function getFeedback(id: string): Promise<FeedbackDetail> {
  return apiClient.get<FeedbackDetail>(`/admin/feedback/${id}`);
}

export function replyFeedback(
  id: string,
  body: FeedbackReplyRequest,
): Promise<FeedbackDetail> {
  return apiClient.post<FeedbackDetail, FeedbackReplyRequest>(
    `/admin/feedback/${id}/reply`,
    body,
  );
}

export function closeFeedback(id: string): Promise<FeedbackDetail> {
  return apiClient.post<FeedbackDetail, Record<string, never>>(
    `/admin/feedback/${id}/close`,
    {},
  );
}

export function reopenFeedback(id: string): Promise<FeedbackDetail> {
  return apiClient.post<FeedbackDetail, Record<string, never>>(
    `/admin/feedback/${id}/reopen`,
    {},
  );
}
